import { getTestUrl } from "./get-test-url";
import { expect, Page, test } from "@playwright/test";

type EventTestConfig = {
    testName: string;
    eventName: string;
    shouldEmit: boolean;
    performAction: (page: Page) => Promise<void>;
    component?: string;
    testVariant?: string;
    hasDetail?: boolean;
    theme?: "web" | "responsive";
};

/**
 * Creates a test for an event.
 *
 * @param {EventTestConfig} config - The configuration for the event test.
 * @param {string} config.testName - The name of the test.
 * @param {string} config.eventName - The name of the event to listen for.
 * @param {boolean} config.shouldEmit - Whether the event should be emitted.
 * @param {function} config.performAction - The action to perform that should trigger the event.
 * @param {string} [config.component] - The component to test. If set, the browser will navigate to the component's URL.
 * @param {string} [config.testVariant] - The variant of the component to test. @example 'default'
 * @param {boolean} [config.hasDetail] - Whether the event has a detail property.
 * @param {"web" | "responsive"} [config.theme] - The theme to use for the test. @default "web"
 *
 * @example
 * defineEventTest({
 *   testName: "should emit uxStartRequest event when loadData is called",
 *   eventName: "uxStartRequest",
 *   shouldEmit: true,
 *   performAction: async (page: Page) => {
 *     await page.click("#loadDataButton");
 *   }
 * });
 * If `config.component` is set, the browser will navigate to the component's URL. If not, the navigation will not be executed, which is useful if you have a `beforeAll` navigation setup.*
 * @example
 * defineEventTest({
 *   testName: "should emit uxStartRequest event when loadData is called",
 *   eventName: "uxStartRequest",
 *   shouldEmit: true,
 *   performAction: async (page: Page) => {
 *     await page.click("#loadDataButton");
 *   },
 *   component: "data-ingest",
 *   testVariant: "default",
 *   hasDetail: true,
 *   theme: "web"
 * });
 */
export const defineEventTest = (config: EventTestConfig) => {
    test(config.testName, async ({ page }) => {
        if (config.component && config.testVariant) {
            await page.goto(getTestUrl(config.component, config.testVariant, config.theme ? config.theme : "web"));
        }
        const eventPromise = page.evaluate(
            ({ eventName, hasDetail }) => {
                return new Promise((resolve) => {
                    document.addEventListener(
                        eventName,
                        (event) => {
                            resolve(hasDetail ? (event as CustomEvent).detail : (event as CustomEvent));
                        },
                        { once: true }
                    );
                    setTimeout(resolve, 5000); // Resolve after 5000ms if no event is fired
                });
            },
            { eventName: config.eventName, hasDetail: config.hasDetail }
        );
        await config.performAction(page);
        const event = await eventPromise;
        if (config.shouldEmit) {
            expect(event).toBeTruthy();
        } else {
            expect(event).toBeFalsy();
        }
    });
};
